import { hasManualCompoundField, manualCompoundFieldValue } from './manualLabOverrides';

/** A reported zero is a measurement; blanks, booleans and nonfinite values are not. */
export function parseLabMeasurement(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}
const record = (value: unknown): Record<string, unknown> => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};

/** Resolve legacy compound metadata without altering the reported weight object. */
export function compoundLabWeight(compound: unknown): Record<string, unknown> {
  const fields = record(compound);
  const weight = record(fields.weight);
  // Legacy concentration aliases are display fallbacks only. A present amount
  // owns its slot, including zero and explicit clears; nested aliases own theirs.
  const hasAmount = Object.prototype.hasOwnProperty.call(weight, 'amount');
  const hasNestedAlias = Object.prototype.hasOwnProperty.call(weight, 'weightMgG');
  const hasAlias = hasNestedAlias || Object.prototype.hasOwnProperty.call(fields, 'weightMgG');
  return {
    ...weight,
    ...(!hasAmount && hasAlias ? { amount: hasNestedAlias ? weight.weightMgG : fields.weightMgG } : {}),
    unit: weight.unit ?? fields.uom,
  };
}

export function selectLabPercent(compound: unknown): number | undefined {
  const fields = record(compound);
  if (fields.percent === null || (typeof fields.percent === 'string' && fields.percent.trim() === '')) return undefined;
  return parseLabMeasurement(fields.percent) ?? parseLabMeasurement(record(fields.weight).percent);
}

/** Select one retained basis without borrowing a different amount after an explicit clear. */
export function selectLabWeight(weight: unknown, unitOverride?: unknown): { value: number | undefined; uom: string } {
  const fields = record(weight);
  const serving = parseLabMeasurement(fields.srv), pkg = parseLabMeasurement(fields.pkg);
  const amount = parseLabMeasurement(fields.amount), ml = parseLabMeasurement(fields.ml), perUnit = parseLabMeasurement(fields.perUnit);
  const sourceUnit = typeof fields.unit === 'string' ? fields.unit : 'mg';
  const unit = typeof unitOverride === 'string' ? unitOverride.includes('/') || !sourceUnit.includes('/')
    ? unitOverride : `${unitOverride}/${sourceUnit.split('/')[1]}` : sourceUnit;
  const denominator = unit.split('/')[1];
  const basis = /^(srv|serving)$/i.test(denominator ?? '') ? 'srv' : /^(pkg|package)$/i.test(denominator ?? '') ? 'pkg'
    : /^ml$/i.test(denominator ?? '') ? 'ml' : /^unit$/i.test(denominator ?? '') ? 'perUnit' : 'amount';
  const selectedField = fields[basis];
  const cleared = selectedField === null || (typeof selectedField === 'string' && selectedField.trim() === '');
  const selected = parseLabMeasurement(selectedField);
  const value = denominator ? cleared ? undefined : selected ?? amount : serving ?? pkg ?? amount ?? ml ?? perUnit;
  // A retained physical clear still identifies its basis when no numeric sibling exists.
  const clearedBasis = ['srv', 'pkg', 'amount', 'ml', 'perUnit'].find(key => fields[key] === null || fields[key] === '');
  const fallbackBasis = serving !== undefined ? 'srv' : pkg !== undefined ? 'pkg' : amount !== undefined ? 'g' : ml !== undefined ? 'ml' : perUnit !== undefined ? 'unit' : clearedBasis === 'perUnit' ? 'unit' : clearedBasis === 'amount' ? 'g' : clearedBasis ?? 'g';
  return { value, uom: denominator ? unit : `${unit}/${fallbackBasis}` };
}

/** A Weight edit changes its displayed basis only; other stored measurements remain separate. */
export function applyLabWeightEdit(weight: unknown, value: unknown, unitOverride?: unknown): Record<string, unknown> {
  const fields = record(weight);
  const denominator = selectLabWeight(fields, unitOverride).uom.split('/')[1];
  const basis = /^(srv|serving)$/i.test(denominator) ? 'srv' : /^(pkg|package)$/i.test(denominator) ? 'pkg'
    : /^ml$/i.test(denominator) ? 'ml' : /^unit$/i.test(denominator) ? 'perUnit' : 'amount';
  return { ...fields, [basis]: value };
}

/** Existing WeightMgG markers always mean concentration, regardless of historical UOM. */
export function applyLabWeightOverride(weight: unknown, value: unknown, _authoredUnit?: unknown): Record<string, unknown> {
  return { ...record(weight), amount: value };
}

export type LabWeightBasis = 'amount' | 'ml' | 'perUnit' | 'srv' | 'pkg';
export const labWeightFields: Record<LabWeightBasis, string> = { amount: 'weightMgG', ml: 'weightMl', perUnit: 'weightPerUnit', srv: 'weightSrv', pkg: 'weightPkg' };
export const labWeightDenominator: Record<LabWeightBasis, string> = { amount: 'g', ml: 'ml', perUnit: 'unit', srv: 'srv', pkg: 'pkg' };
export function validLabWeightBasis(value: unknown): LabWeightBasis | undefined {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(labWeightFields, value) ? value as LabWeightBasis : undefined;
}
export function inferredLabWeightBasis(weight: unknown): LabWeightBasis {
  const denominator = selectLabWeight(weight).uom.split('/')[1];
  return /^(srv|serving)$/i.test(denominator) ? 'srv' : /^(pkg|package)$/i.test(denominator) ? 'pkg'
    : /^ml$/i.test(denominator) ? 'ml' : /^unit$/i.test(denominator) ? 'perUnit' : 'amount';
}
export function manualLabWeightBasis(weight: unknown, intent: unknown, enabled: string): LabWeightBasis | undefined {
  const explicit = validLabWeightBasis(manualCompoundFieldValue(intent, enabled, 'weightBasis'));
  const selected = (enabled.startsWith('lab_tp_') && explicit !== 'amount' ? undefined : explicit)
    ?? (hasManualCompoundField(intent, enabled, 'weightMgG') ? 'amount' : undefined);
  if (selected || !enabled.startsWith('lab_tp_')) return selected;
  const fields = record(weight);
  if (typeof fields.unit === 'string' && fields.unit.includes('/')) return undefined;
  // Preserve raw bare-unit priority. Only an authored, stored/projected clear
  // owns its selected slot; missing/unmarked values still use legacy fallback.
  for (const basis of ['srv', 'pkg', 'amount', 'ml', 'perUnit'] as const) {
    const value = fields[basis];
    if (parseLabMeasurement(value) !== undefined) return undefined;
    if ((basis === 'srv' || basis === 'pkg') && hasManualCompoundField(intent, enabled, labWeightFields[basis])
      && (value === null || (typeof value === 'string' && value.trim() === ''))) return basis;
  }
  return undefined;
}
/** Public callers select actual stored values, never the numeric marker itself. */
export function selectManualLabWeight(weight: unknown, intent: unknown, enabled: string, unitOverride?: unknown): { value: number | undefined; uom: string } {
  const basis = manualLabWeightBasis(weight, intent, enabled);
  if (!basis) return selectLabWeight(weight, unitOverride);
  const fields = record(weight);
  const sourceUnit = typeof unitOverride === 'string' ? unitOverride : typeof fields.unit === 'string' ? fields.unit : 'mg';
  const reportedDenominator = sourceUnit.split('/')[1] ?? (typeof fields.unit === 'string' ? fields.unit.split('/')[1] : undefined);
  const matchingDenominator = reportedDenominator && inferredLabWeightBasis({ unit: `mg/${reportedDenominator}` }) === basis ? reportedDenominator : labWeightDenominator[basis];
  return { value: parseLabMeasurement(fields[basis]), uom: `${sourceUnit.split('/')[0]}/${matchingDenominator}` };
}
/** The editor projects explicit physical intent without changing raw reported evidence. */
export function projectManualLabWeight(weight: unknown, intent: unknown, enabled: string): Record<string, unknown> {
  const result = { ...record(weight) };
  const authoredUnit = manualCompoundFieldValue(intent, enabled, 'uom');
  if (typeof authoredUnit === 'string') result.unit = authoredUnit;
  for (const [basis, suffix] of Object.entries(labWeightFields)) {
    if (hasManualCompoundField(intent, enabled, suffix)) result[basis] = manualCompoundFieldValue(intent, enabled, suffix);
  }
  return result;
}

/** The editor's established bare-unit priority differs from legacy public presentation. */
export function selectEditorLabWeight(weight: unknown, intent?: unknown, enabled = ''): { value: number | undefined; uom: string } {
  if (manualLabWeightBasis(weight, intent, enabled)) return selectManualLabWeight(weight, intent, enabled);
  const fields = record(weight);
  if (typeof fields.unit === 'string' && fields.unit.includes('/')) return selectLabWeight(fields);
  const order: LabWeightBasis[] = ['amount', 'pkg', 'srv', 'ml', 'perUnit'];
  const basis = order.find(key => parseLabMeasurement(fields[key]) !== undefined)
    ?? order.find(key => fields[key] === null || fields[key] === '') ?? 'amount';
  return { value: parseLabMeasurement(fields[basis]), uom: `${typeof fields.unit === 'string' ? fields.unit : 'mg'}/${labWeightDenominator[basis]}` };
}
export function inferredEditorLabWeightBasis(weight: unknown): LabWeightBasis {
  return inferredLabWeightBasis({ unit: selectEditorLabWeight(weight).uom });
}
